/* eslint-disable react/jsx-props-no-spreading */
/* eslint-disable @typescript-eslint/no-explicit-any */
/* -------------------------- Design imports start -------------------------- */
import { Add } from '@mui/icons-material'
import {
  TextField,
  type TextFieldProps,
  Autocomplete,
  type AutocompleteRenderOptionState,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  CircularProgress,
} from '@mui/material'
/* --------------------------- Design imports end --------------------------- */

/* ------------------------ Functional imports start ------------------------ */
import React, { type HTMLAttributes, type ReactElement, useCallback, useEffect, useMemo, useState } from "react";
/* ------------------------- Functional imports end ------------------------- */

type Props = {
  id?: string
  name: string
  value: any[]
  errorMessage?: string
  options?: { value: string | number | boolean | null; label?: string; disabled?: boolean }[]
  loadData?: () => Promise<
    { value: string | number | boolean | null; label?: string; disabled?: boolean }[]
  >
  loadAllData?: () => Promise<
    { value: string | number | boolean | null; label?: string; disabled?: boolean }[]
  >
  disabled?: boolean
  required?: boolean
  onChange: (value: any[]) => void
  validation?: boolean
  onAddOption?: () => void
  // Autocomplete-specific props
  useAutocomplete?: boolean
  autocompleteHook?: {
    suggestions: Array<{ id: string; name: string } | { value: string; label: string }>
    isLoading: boolean
    updateQuery: (query: string) => void
    query: string
  }
}
/* -------------------------------------------------------------------------- */
/*                               Start Component                              */
/* -------------------------------------------------------------------------- */
export default function SearchMultiSelectInput<T>(props: Props): ReactElement {
  /* -------------------------- Non state data start -------------------------- */
  const {
    id,
    name,
    value,
    options,
    loadData,
    disabled,
    loadAllData,
    errorMessage,
    validation,
    onChange,
    required,
    onAddOption,
    useAutocomplete,
    autocompleteHook,
  } = props
  /* --------------------------- Non state data end --------------------------- */
  /* ---------------------------- Flag states start --------------------------- */
  const [error, setError] = useState<boolean>(!!(!value && required))
  const [loading, setLoading] = useState<boolean>(false)
  /* ----------------------------- Flag states end ---------------------------- */
  /* ------------------------------Data states start--------------------------- */
  const [newOptions, setNewOptions] = useState<
    { value: string | number | boolean | null; label?: string; disabled?: boolean }[]
  >(options || [])
  /* ------------------------------ Data states end --------------------------- */

  useEffect(() => {
    if (!value && required) {
      setError(true)
    }
    if (validation !== false && !value) {
      setError(true)
    }
  }, [value])

  // Convert autocomplete suggestions to options format
  useEffect(() => {
    if (useAutocomplete && autocompleteHook) {
      const convertedOptions = autocompleteHook.suggestions.map(suggestion => {
        // Handle both {id, name} and {value, label} formats
        if ('id' in suggestion && 'name' in suggestion) {
          return { value: (suggestion as any).id, label: (suggestion as any).name }
        }
        // Handle user suggestions: { id, full_name, email }
        if ('id' in suggestion && 'full_name' in suggestion) {
          const s: any = suggestion
          const label = s.full_name || s.email || String(s.id)
          return { value: s.id, label }
        }
        return suggestion as { value: string; label: string }
      })
      setNewOptions(convertedOptions)
    }
  }, [useAutocomplete, autocompleteHook?.suggestions])

  const handleOpen = useCallback(async () => {
    // If using autocomplete, trigger initial load of popular items
    if (useAutocomplete && autocompleteHook) {
      // Trigger query with empty string to load popular items
      if (autocompleteHook.query === '') {
        autocompleteHook.updateQuery('')
      }
    }
    // Legacy: Load data if not using autocomplete
    else if (!useAutocomplete && loadData) {
      setLoading(true)
      const data = await loadData()
      setNewOptions(data)
      if (loadAllData) {
        const fulldata = await loadAllData()
        setNewOptions([])
        setNewOptions(fulldata)
      }
      setLoading(false)
    }
  }, [loadData, loadAllData, useAutocomplete, autocompleteHook])

  const handleInputChange = useCallback((
    _event: React.SyntheticEvent, 
    newInputValue: string, 
    reason: string
  ) => {
    // Update autocomplete hook if enabled and user is typing
    if (useAutocomplete && autocompleteHook && reason === 'input') {
      autocompleteHook.updateQuery(newInputValue)
    }
  }, [useAutocomplete, autocompleteHook])

  const parseValue = (value: any[]) => {
    if (Array.isArray(value)) {
      if (value[0] && Object.keys(value[0]).includes('label')) {
        return value
      } else {
        if (value) {
          return value.map((v: any) => {
            // Prefer explicit label if present on value
            if (v && v.label) return v
            // Users: support full_name/email
            if (v && (v.full_name || v.email)) {
              return { value: v.id ?? v.value ?? v, label: v.full_name || v.email || String(v) }
            }
            // Fallback first/last name
            if (v && (v.firstName || v.lastName)) {
              return { value: v, label: `${v.firstName ?? ''} ${v.lastName ?? ''}`.trim() }
            }
            // Generic fallback
            return { value: v, label: String(v?.label ?? v?.value ?? v ?? '') }
          })
        } else {
          return []
        }
      }
    } else {
      if (value) {
        const v: any = value
        const label = v?.label || v?.full_name || v?.email || `${v?.firstName ?? ''} ${v?.lastName ?? ''}`.trim() || String(v)
        return [{ value: v?.id ?? v?.value ?? v, label }]
      } else {
        return []
      }
    }
  }

  /* ------------------------- Render constants start ------------------------- */
  const renderOption = (
    props: HTMLAttributes<HTMLLIElement>,
    option: any,
    state: AutocompleteRenderOptionState
  ) => {
    if (loading && option.value === 'add') {
      return (
        <ListItem
          key={option.value}
          style={{ display: 'flex', alignItems: 'center', zIndex: 9999 }}
          disablePadding
        >
          <ListItemButton dense>
            <ListItemIcon>
              <CircularProgress color="inherit" size={20} />
            </ListItemIcon>
          </ListItemButton>
        </ListItem>
      )
    }
    if (!onAddOption && option.value === 'add') {
      return null
    }
    if (option.value === 'add') {
      return (
        <ListItem
          key={option.value}
          style={{ display: 'flex', alignItems: 'center' }}
          disablePadding
        >
          <ListItemButton id="addOption" onClick={onAddOption} dense>
            <ListItemIcon>
              <Add />
            </ListItemIcon>
          </ListItemButton>
        </ListItem>
      )
    }
    const optionKey = option?.value?.self ?? option?.value ?? option.label

    return (
      <ListItem
        {...props}
        key={String(optionKey ?? option.label ?? '')}
        style={{ display: 'flex', alignItems: 'center' }}
      >
        <ListItemText primary={option.label} />
      </ListItem>
    )
  }
  /* -------------------------- Render constants end -------------------------- */

  /* -------------------------------------------------------------------------- */
  /*                              Render component                              */
  /* -------------------------------------------------------------------------- */
  const parsedValue = useMemo(() => parseValue(value), [value])

  return (
    <Autocomplete
      id={id || name}
      openOnFocus
      onOpen={handleOpen}
      onInputChange={handleInputChange}
      multiple
      disabled={disabled}
      loading={useAutocomplete ? (autocompleteHook?.isLoading ?? false) : loading}
      getOptionLabel={(option: any) => option?.label ?? String(option?.value ?? '')}
      isOptionEqualToValue={(option, currentValue) => {
        if (option.value === 'add' || currentValue?.value === 'add') {
          return false
        }

        const optionKey = option?.value?.self ?? option?.value
        const valueKey =
          currentValue?.value?.self ?? currentValue?.value ?? currentValue?.self ?? currentValue

        return optionKey === valueKey
      }}
      loadingText={<CircularProgress color="inherit" size={20} />}
      options={[{ value: 'add', label: 'add' }, ...newOptions] as any[]}
      value={parsedValue}
      filterOptions={(options: any[], state: any) => {
        // Skip filtering if using autocomplete (filtering is done server-side)
        if (useAutocomplete) {
          return options
        }
        if (state.inputValue !== '' && state.index !== -1) {
          return options.filter(option =>
            option.label?.toLowerCase().includes(state.inputValue.toLowerCase())
          )
        } else {
          return options
        }
      }}
      renderOption={renderOption}
      disablePortal /* List of suggestions will not be rendered on top of everything */
      onChange={(_: any, option: any, reason: string) => {
        onChange(option)
        option !== '' ? setError(false) : setError(true)
      }}
      renderInput={(params: JSX.IntrinsicAttributes & TextFieldProps) => (
        <TextField
          id={id || name}
          required={required}
          {...params}
          error={error}
          helperText={error && errorMessage}
          label={name}
          variant="outlined"
        />
      )}
    />
  )
}
